import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CircleNotch,
  Warning,
  CheckCircle,
  LockSimpleOpen,
  FolderOpen,
  DownloadSimple,
  Receipt,
  Gavel,
  FileText,
  ClockCounterClockwise,
  ArrowLeft,
  ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  checkMatterClosure,
  closeMatter,
  fetchClosureSummary,
  downloadClosingSummaryPdf,
  CLOSURE_REASON_LABELS,
  type ClosureReason,
  type ClosureCheckResult,
  type ClosureSummary,
} from '@/lib/mattersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

const BLOCKER_DESCRIPTIONS: Record<string, string> = {
  open_tasks: 'open tasks',
  pending_hearings: 'upcoming hearings',
  draft_fee_notes: 'draft fee notes',
  outstanding_fee_notes: 'outstanding fee notes',
  active_allocations: 'active brief offers',
};

interface Props {
  matterId: string;
  matterNumber: string;
  matterName: string;
  onClose: () => void;
  onClosed: () => void;
}

export function CloseMatterModal({
  matterId,
  matterNumber,
  matterName,
  onClose,
  onClosed,
}: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'review' | 'confirm'>('review');
  const [closureReason, setClosureReason] = useState<ClosureReason>('settled');
  const [closureNote, setClosureNote] = useState('');
  const [forceCloseReason, setForceCloseReason] = useState('');
  const [showForce, setShowForce] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // The "this case is finished" picture — shown before the close controls.
  const summaryQ = useQuery<ClosureSummary>({
    queryKey: ['matter-closure-summary', matterId],
    queryFn: () => fetchClosureSummary(matterId),
    staleTime: 30_000,
  });

  const checkQ = useQuery<ClosureCheckResult>({
    queryKey: ['matter-closure-check', matterId, closureReason],
    queryFn: () => checkMatterClosure(matterId, closureReason),
    // Only needed on the confirm step — keeps the review step a pure read.
    enabled: step === 'confirm',
    staleTime: 30_000,
  });

  async function handleDownloadPdf() {
    setDownloading(true);
    try {
      await downloadClosingSummaryPdf(matterId, matterNumber);
    } catch {
      toast.error('Could not generate the closing summary PDF');
    } finally {
      setDownloading(false);
    }
  }

  const closeMut = useMutation({
    mutationFn: (opts: { force: boolean }) =>
      closeMatter(
        matterId,
        closureReason,
        closureNote.trim(),
        opts.force,
        opts.force ? forceCloseReason : undefined,
      ),
    onSuccess: () => {
      toast.success('Matter closed');
      qc.invalidateQueries({ queryKey: ['matter-by-id', matterId] }); // was ['matter', id] — a key NO component reads; the reader is ['matter-by-id', id]
      qc.invalidateQueries({ queryKey: ['matters'] });
      onClosed();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not close matter')),
  });

  const check = checkQ.data;
  const hasBlockers = (check?.blockers?.length ?? 0) > 0;
  // WHO MAY OVERRIDE IS THE SERVER'S ANSWER, NOT THIS COMPONENT'S GUESS. `canForceClose` arrives
  // with the closure check and was never read: the override box was shown to anyone who hit a
  // blocker, so a member type the server refuses could tick it, write an override reason, press
  // "Close anyway" and get back a refusal that merely repeated the blockers.
  const canForceClose = hasBlockers && (check?.canForceClose ?? false);
  // No closure note, no close — the same rule the server applies, said on the form rather than
  // discovered by pressing the button and reading an error.
  const missingNote = !closureNote.trim();
  // A stale tick cannot survive the answer changing under it (a refetch, or a different reason
  // producing different blockers).
  const forcing = showForce && canForceClose;

  const s = summaryQ.data;
  const fmtMoney = (v: number, currency: string) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 }).format(v);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !closeMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex w-full max-w-lg flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <FolderOpen size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Close matter
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {matterNumber} · {matterName}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !closeMut.isPending && onClose()}
            disabled={closeMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {/* ─── Step 1: Closure review — "this case is finished" ─── */}
          {step === 'review' && (
            <>
              {summaryQ.isLoading && (
                <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
                  <CircleNotch size={14} className="animate-spin" />
                  Gathering the final picture of this matter…
                </div>
              )}

              {summaryQ.isError && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-3 py-2.5">
                  <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Could not load the closure summary. You can still close the matter below.
                  </p>
                </div>
              )}

              {s && (
                <>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Before closing, here is the complete picture of this matter — its money, its
                    documents, and its history. Review it, keep a copy, then sign it off.
                  </p>

                  {/* Financial position — absent when the viewer holds no fees access (server decides) */}
                  {s.finance && (
                  <div className="grid grid-cols-3 divide-x divide-border rounded-md border border-border">
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Fees raised</p>
                      <p className="mt-0.5 text-sm font-semibold tabular-nums">{fmtMoney(s.finance.raised, s.finance.currency)}</p>
                    </div>
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Payments received</p>
                      <p className="mt-0.5 text-sm font-semibold tabular-nums">{fmtMoney(s.finance.collected, s.finance.currency)}</p>
                    </div>
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Outstanding</p>
                      <p className={`mt-0.5 text-sm font-semibold tabular-nums ${s.finance.outstanding > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                        {fmtMoney(s.finance.outstanding, s.finance.currency)}
                        {s.finance.outstanding === 0 && <CheckCircle size={12} weight="fill" className="ml-1 inline align-text-top text-emerald-600 dark:text-emerald-400" />}
                      </p>
                    </div>
                  </div>
                  )}

                  {/* File at a glance */}
                  <div className="space-y-1.5 rounded-md border border-border px-3 py-2.5">
                    <Row icon={<Gavel size={14} weight="duotone" className="text-muted-foreground" />}
                      label="Hearings"
                      value={`${s.hearings.total} total · ${s.hearings.completed} completed`} />
                    {s.finance && (
                      <Row icon={<Receipt size={14} weight="duotone" className="text-muted-foreground" />}
                        label="Fee notes"
                        value={`${s.finance.feeNoteCount} issued`} />
                    )}
                    <Row icon={<FileText size={14} weight="duotone" className="text-muted-foreground" />}
                      label="Documents on file"
                      value={`${s.documents.count}`} />
                    <Row icon={<ClockCounterClockwise size={14} weight="duotone" className="text-muted-foreground" />}
                      label="Timeline events"
                      value={`${s.timeline.count}`} />
                  </div>

                  {/* Archive package */}
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                  >
                    {downloading ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
                    Download closing summary (PDF)
                  </button>
                </>
              )}
            </>
          )}

          {/* ─── Step 2: Confirm closure ─── */}
          {step === 'confirm' && (
            <>
          {/* Reason selector */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Closure reason
            </label>
            <select
              value={closureReason}
              onChange={(e) => {
                setClosureReason(e.target.value as ClosureReason);
                setShowForce(false);
              }}
              disabled={closeMut.isPending}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              {(Object.entries(CLOSURE_REASON_LABELS) as [ClosureReason, string][]).map(
                ([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* The note is the file's record of WHY — required, like the server now requires it. */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Closure note <span className="text-red-500">*</span>
            </label>
            <ExpandableTextarea
              label="Closure note"
              value={closureNote}
              onChange={setClosureNote}
              rows={2}
              maxLength={2000}
              placeholder="Why is this matter being closed? e.g. Settled at the door of the court; client advised in writing."
              disabled={closeMut.isPending}
            />
          </div>

          {/* Closure check status */}
          {checkQ.isLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CircleNotch size={12} className="animate-spin" />
              Checking for open items…
            </div>
          )}

          {check && !hasBlockers && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2.5">
              <CheckCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <p className="text-xs text-emerald-800 dark:text-emerald-300">
                No open items found — this matter can be closed.
              </p>
            </div>
          )}

          {check && hasBlockers && (
            <div className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-3 py-3">
              <div className="mb-2 flex items-center gap-2">
                <Warning size={14} weight="fill" className="shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                  This matter has open items that should be resolved before closing:
                </p>
              </div>
              <ul className="space-y-1 pl-5">
                {check.blockers.map((b) => (
                  <li key={b.type} className="text-xs text-amber-800 dark:text-amber-300">
                    <span className="font-medium">{b.count}</span>{' '}
                    {BLOCKER_DESCRIPTIONS[b.type] ?? b.type}
                    {b.items.length > 0 && (
                      <span className="ml-1 text-amber-700 dark:text-amber-300/70">
                        ({b.items.map((i) => i.summary).join(', ')}
                        {b.count > b.items.length ? ` and ${b.count - b.items.length} more` : ''})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Close-anyway checkbox — only when there are open items AND this member type may
              override them. Everyone else is told plainly that the items come first. */}
          {hasBlockers && !canForceClose && (
            <p className="text-xs text-muted-foreground">
              These items have to be resolved before this matter can be closed.
            </p>
          )}
          {canForceClose && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={showForce}
                onChange={(e) => setShowForce(e.target.checked)}
                disabled={closeMut.isPending}
                className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-600"
              />
              <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <LockSimpleOpen size={12} weight="duotone" />
                Close anyway, ignoring the open items above
              </span>
            </label>
          )}

          {forcing && (
            <div className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/10 px-3 py-3">
              <div className="mb-2 flex items-center gap-2">
                <Warning size={14} weight="fill" className="shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-xs font-semibold text-red-800 dark:text-red-300">Closing with open items</p>
              </div>
              <p className="mb-2 text-xs text-red-700 dark:text-red-300">
                You are closing this matter despite unresolved items. Open tasks will be
                marked completed (matter closed); allocations remain on the record. Provide a reason for the audit log.
              </p>
              <ExpandableTextarea
                label="Reason for closing anyway"
                value={forceCloseReason}
                onChange={setForceCloseReason}
                rows={2}
                maxLength={2000}
                placeholder="Reason — required"
                disabled={closeMut.isPending}
              />
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            All records, documents, and financial history are retained. The matter can be
            restored at any time.
          </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          {step === 'confirm' ? (
            <button
              type="button"
              onClick={() => setStep('review')}
              disabled={closeMut.isPending}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <ArrowLeft size={13} weight="bold" />
              Back to review
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={closeMut.isPending}
            >
              Cancel
            </Button>

            {step === 'review' ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setStep('confirm')}
                className="gap-1.5"
              >
                Continue to close
                <ArrowRight size={14} weight="bold" />
              </Button>
            ) : forcing ? (
              <Button
                type="button"
                size="sm"
                disabled={closeMut.isPending || missingNote || !forceCloseReason.trim()}
                onClick={() => closeMut.mutate({ force: true })}
                className="gap-1.5 bg-red-600 text-white hover:bg-red-700"
              >
                {closeMut.isPending ? (
                  <CircleNotch size={14} className="animate-spin" />
                ) : (
                  <LockSimpleOpen size={14} weight="fill" />
                )}
                Close anyway
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={closeMut.isPending || checkQ.isLoading || hasBlockers || missingNote}
                onClick={() => closeMut.mutate({ force: false })}
                className="gap-1.5"
              >
                {closeMut.isPending || checkQ.isLoading ? (
                  <CircleNotch size={14} className="animate-spin" />
                ) : (
                  <FolderOpen size={14} weight="fill" />
                )}
                Close matter
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="font-medium tabular-nums text-foreground">{value}</span>
    </div>
  );
}
